import {
  CustomUrlStatus,
  Iso8601Date,
  OrganizationPublicId,
  PrivacyCenterNoticePublicId,
  PrivacyCenterNoticeStatus,
  PrivacyCenterNoticeType,
  PrivacyCenterPublicId,
  PrivacyPolicySectionPublicId,
  UUIDString,
} from "../Types";
import {
  RegulationRequestTypeDto,
  SimplifiedRegionRequestTypeDto,
} from "./RegulationRequestTypeDto";

// Custom field definitions for DSAR forms (Crunch Fitness integration)
export interface CustomFieldDefinitionDto {
  fieldNameCode: string;
  fieldNameDisplay: string;
  fieldType: string; // TEXT, BOOLEAN, DROPDOWN
  fieldOptions?: FieldOptionDto[];
  required?: boolean;
}

export interface FieldOptionDto {
  value: string;
  display: string;
  name?: string;
  type?: string;
  state?: string; // For location-based filtering
}

// Read objects

export enum OptOutType {
  NONE = "NONE",
  SHORT = "SHORT",
  LONG = "LONG",
}

export interface PrivacyCenterDto {
  id: PrivacyCenterPublicId;
  organizationId: OrganizationPublicId;
  name: string;
  requestEmail: string;

  faviconUrl: string;
  logoUrl: string;
  logoLinkUrl: string;
  activeColor: string;
  inactiveColor: string;
  backgroundColor: string;
  mainHeaderFontFamily?: string;
  mainHeaderFontSize?: number;
  mainHeaderFont: PrivacyCenterFontDto;
  bodyFontFamily?: string;
  bodyFontSize?: number;
  bodyFont: PrivacyCenterFontDto;
  navigationFontFamily?: string;
  navigationFontSize?: number;
  navigationFont: PrivacyCenterFontDto;
  sectionHeaderFontFamily?: string;
  sectionHeaderFontSize?: number;
  sectionHeaderFont: PrivacyCenterFontDto;
  welcomeText: string;
  titleText: string;
  defaultSubdomain: string;
  customUrl?: string;
  customUrlStatus?: CustomUrlStatus;
  customUrlCnameReady?: boolean;
  url?: string;
  hasMobileApplication?: boolean;
  optOutUrl?: string;
  optOutFormOptions: {
    optOutType: OptOutType;
    limit: boolean;
    profilingOptOut: boolean;
  };
  hideCcpaSection?: boolean;

  doNotSellMarkdown?: string;
  employeePrivacyNoticeMarkdown?: string;
  jobApplicantPrivacyNoticeMarkdown?: string;

  dpoOfficerName: string | undefined;
  dpoOfficerEmail: string | undefined;
  dpoOfficerPhone: string | undefined;
  dpoOfficerAddress: string | undefined;
  dpoOfficerDoesNotApply: boolean;

  eeaContactName: string | undefined;
  eeaContactAddress: string | undefined;
  eeaContactEmail: string | undefined;
  eeaContactPhone: string | undefined;
  eeaContactDoesNotApply: boolean | undefined;
  ropaDoesNotApply: boolean | undefined;

  pipedaOfficerName: string | undefined;
  pipedaOfficerEmail: string | undefined;
  pipedaOfficerPhone: string | undefined;
  pipedaOfficerAddress: string | undefined;

  requiresDpo: boolean;
  hasDpoTask: boolean;

  hasDesignatedRepTask: boolean;

  disableCaResidencyConfirmation: boolean;
  hideLocationDropdownOnRequestForm: boolean;
  addOtherOptionToLocationDropdownOnRequestForm: boolean;
  showExtendedAddressFormForOptOut: boolean;
  showExtendedAddressFormForRequests: boolean;
  showPhoneNumberFormForOptOut: boolean;
  showPhoneNumberFormForRequests: boolean;

  ccpaPrivacyNoticeIntroText: string;
  vcdpaPrivacyNoticeIntroText: string;
  cpaPrivacyNoticeIntroText: string;
  ctdpaPrivacyNoticeIntroText: string;
  tdpsaPrivacyNoticeIntroText: string;
  ocpaPrivacyNoticeIntroText: string;
  utcpaPrivacyNoticeIntroText: string;
  fdbrPrivacyNoticeIntroText: string;
  mcdpaPrivacyNoticeIntroText: string;
  icdpaPrivacyNoticeIntroText: string;
  dpdpaPrivacyNoticeIntroText: string;
  nhpaPrivacyNoticeIntroText: string;
  ndpaPrivacyNoticeIntroText: string;
  njdpaPrivacyNoticeIntroText: string;
  npicicaPrivacyNoticeIntroText: string;
  modpaPrivacyNoticeIntroText: string;
  gdprPrivacyNoticeIntroText: string;
  pipedaPrivacyNoticeIntroText: string;
  cookiePolicyIntroText: string;
  retentionIntroText: string;
  optOutNoticeIntroText: string;

  domains?: PrivacyCenterDomainDto[];

  gaMeasurementId?: string;
  cmpVersion?: string;
  allowWebIndexing: boolean;
  notices: PrivacyCenterNoticeDto[];
  anchorsByState?: string[];
  hiddenStateSectionAnchors?: string[];
  requestTypes?: RegulationRequestTypeDto[];
  regionRequestTypeMap?: Record<string, SimplifiedRegionRequestTypeDto>;
  customFields?: CustomFieldDefinitionDto[];
  collectsBiometricData?: boolean;
}

export interface PrivacyCenterDomainDto {
  id: string;
  customDomain: string;
  certificateStatus?: string;
  expiresAt?: Iso8601Date;
}

export interface PrivacyCenterNoticeDto {
  id: UUIDString;
  publicId: string;

  privacyCenterId: string;
  type: PrivacyCenterNoticeType;
  status: PrivacyCenterNoticeStatus;
  name: string;
  slug: string;
  version: number;
  versionName?: string;

  sections: PrivacyCenterNoticeSectionDto[];

  hiddenStateSectionAnchors: string[];
  useCustomRetentionPolicySection: boolean;

  createdAt: Iso8601Date;
  publishedAt?: string;
  lastModifiedAt: Iso8601Date;
  isMigratedFromPreviousPc: boolean;
}

export interface PrivacyCenterNoticeSectionDto {
  id: UUIDString;

  privacyCenterNoticeId?: string;
  displayOrder: number;
  header: string;
  anchor: string;
  addToNavigation: boolean;
  content: string;
  required: boolean;
  locked: boolean;
  custom: boolean;
  placeholder?: string;
  refersToTemplate?: string;

  createdAt: Iso8601Date;
  lastModifiedAt: Iso8601Date;
}

export interface PrivacyCenterFontDto {
  id: string;
  name: string;
  url: string;
  isCustom: boolean;
}

export interface PrivacyCenterPolicyDto {
  privacyCenterId: PrivacyCenterPublicId;
  policyLastUpdated?: Iso8601Date;
  policySections: PrivacyCenterPolicySectionDto[];
  renderedPolicySections: PrivacyCenterPolicySectionDto[];
}

export interface PrivacyCenterPolicySectionDto {
  id: PrivacyPolicySectionPublicId;
  custom: boolean;
  privacyCenterId?: PrivacyCenterPublicId;
  name?: string;
  anchor?: string;
  addToMenu?: boolean;
  noPrivacyPolicy?: boolean;
  body?: string;
  displayOrder: number;
}

// Write objects

export type CreatePrivacyCenter = {
  name: string;
  faviconUrl: string;
  defaultSubdomain: string;
  customUrl?: string;
};

export type UpdatePrivacyCenter = {
  name?: string;
  faviconUrl?: string;
  activeColor?: string;
  inactiveColor?: string;
  backgroundColor?: string;
  mainHeaderFontFamily?: string;
  mainHeaderFontSize?: number;
  mainHeaderFontId?: string;
  bodyFontFamily?: string;
  bodyFontSize?: number;
  bodyFontId?: string;
  navigationFontFamily?: string;
  navigationFontSize?: number;
  navigationFontId?: string;
  sectionHeaderFontFamily?: string;
  sectionHeaderFontSize?: number;
  sectionHeaderFontId?: string;
  welcomeText?: string;
  titleText?: string;
  defaultSubdomain?: string;
  customUrl?: string;
  customFieldEnable?: boolean;
  customFieldLabel?: string;
  customFieldHelp?: string;

  dpoOfficerName?: string;
  dpoOfficerEmail?: string;
  dpoOfficerPhone?: string;
  dpoOfficerAddress?: string;
  dpoOfficerDoesNotApply?: boolean;
  eeaContactName?: string;
  eeaContactAddress?: string;
  eeaContactEmail?: string;
  eeaContactPhone?: string;
  eeaContactDoesNotApply?: boolean;
  ropaDoesNotApply?: boolean;
  ccpaPrivacyNoticeIntroText?: string;
  gdprPrivacyNoticeIntroText?: string;
  pipedaPrivacyNoticeIntroText?: string;
  vcdpaPrivacyNoticeIntroText?: string;
  npicicaPrivacyNoticeIntroText?: string;
  modpaPrivacyNoticeIntroText?: string;
  cookiePolicyIntroText?: string;
  optOutNoticeIntroText?: string;
};

export type UpdatePrivacyCenterPolicy = {
  policyLastUpdated: Iso8601Date;
  policySectionsOrder: Record<UUIDString, number>;
};

export type AddOrUpdatePrivacyCenterPolicySection = {
  name: string;
  anchor: string;
  addToMenu: boolean;
  noPrivacyPolicy: boolean;
  body: string;
  publish?: boolean;
};

export interface CreatePrivacyCenterNoticeRequest {
  type: PrivacyCenterNoticeType;
  fromPreviousNoticeId?: string;
}

export interface CreatePrivacyCenterNoticeSectionRequest {
  id?: string;
  displayOrder?: number;
  header?: string;
  anchor?: string;
  addToNavigation?: boolean;
  content?: string;
  required?: boolean;
  locked?: boolean;
  custom?: boolean;
  placeholder?: string;
  refersToTemplate?: string;
}

export interface CreatePrivacyCenterNoticeSectionBatchRequest {
  sections: CreatePrivacyCenterNoticeSectionRequest[];
}

export interface UpdatePrivacyCenterNoticeRequest {
  status?: PrivacyCenterNoticeStatus;
  name?: string;
  slug?: string;
  sections?: UpdatePrivacyCenterNoticeSectionRequest[];
  publishedAt?: string;
  useCustomRetentionPolicySection?: boolean;
}

export interface UpdatePrivacyCenterNoticeWithIdRequest extends UpdatePrivacyCenterNoticeRequest {
  id: PrivacyCenterNoticePublicId;
}

export interface UpdatePrivacyCenterNoticeSectionRequest {
  displayOrder?: number;
  header?: string;
  anchor?: string;
  addToNavigation?: boolean;
  content?: string;
  locked?: boolean;
  placeholder?: string;
}

export interface UpdatePrivacyCenterNoticeSectionWithIdRequest
  extends UpdatePrivacyCenterNoticeSectionRequest {
  id: UUIDString;
}

export interface NoticeEditorMetadataResponse {
  availableEmbeddedAssets: Record<string, boolean>;
  gdprTemplate: string;
  pipedaTemplate: string;
  hasShopifySNIDataRecipient: boolean;
}
